import { FormGroup } from "@blueprintjs/core";
import {
  MultiSelectChangeEventArgs,
  MultiSelectComponent,
} from "@syncfusion/ej2-react-dropdowns";
import React from "react";
import { FieldLabel } from "../../CoreFields/FieldLabel/FieldLabel";
import { FormFieldErrorContainer } from "../../CoreFields/FieldError/FormFieldErrorContainer";
import {
  BaseEnumFieldProps,
  EnumMultiSelectFieldSingleValue,
  EnumMultiSelectFieldV2Value,
  FieldLabeledProps,
} from "../../types/fieldTypes";
import { useEnumMultiSelectFieldV2 } from "./useEnumMultiSelectFieldV2";
import classNames from "classnames";

export interface EnumMultiSelectFieldV2Props
  extends BaseEnumFieldProps<
    EnumMultiSelectFieldV2Value,
    EnumMultiSelectFieldSingleValue
  > {
  placeholder?: string;
  labelProps?: FieldLabeledProps;
  noRecordsTemplate?: (ref: any) => React.ReactNode;
  allowCustomValue?: boolean;
  growToFit?: boolean;
}

const EnumMultiSelectFieldV2: React.FC<EnumMultiSelectFieldV2Props> = (
  props
) => {
  const {
    localValue,
    setNoRecordsTemplate,
    multiSelectRef,
    handleValueChange,
  } = useEnumMultiSelectFieldV2(props);

  setNoRecordsTemplate();

  return (
    <FormGroup
      className={`${
        (props.isRequired && !localValue) ||
        (props.isRequired && !!localValue && localValue.length === 0) ||
        (props.isRequired &&
          !!props.value &&
          props.value.filter((x) => x !== null && x !== undefined).length === 0)
          ? "has-required-background"
          : "ucl-enum-multiselect-field-v2"
      }`}
    >
      {props.label && (
        <FieldLabel
          labelName={props.label}
          tooltipDescription={props.description}
          fieldProps={{ ...props, value: localValue }}
          isRequired={props.isRequired}
          {...props.labelProps}
        />
      )}
      <div className="ucl-enum-multiselect-field-v2-toolbar">
        <div
          className={classNames(
            "ucl-enum-multiselect-field-v2-input-container",
            props.growToFit && "ucl-enum-multiselect-field-v2-grow-to-fit"
          )}
        >
          <MultiSelectComponent
            className={"ucl-enum-multiselect-field-v2-input"}
            ref={multiSelectRef}
            value={localValue as number[]}
            dataSource={
              props.optionValues as unknown as { [key: string]: Object }[]
            }
            fields={{ text: "label", value: "value" }}
            changeOnBlur={false}
            allowCustomValue={props.allowCustomValue || false}
            customValueSelection={(e: any) => {
              if (props.allowCustomValue) {
                e.newData = { label: e.text, value: e.text };
              }
            }}
            onChange={(e: { value: EnumMultiSelectFieldV2Value }) => {
              handleValueChange(e.value);
            }}
            placeholder={props.placeholder}
            itemTemplate="<table><tr><td>${label}</td></tr><table>"
            change={async (e: MultiSelectChangeEventArgs) => {
              // Syncfusion emits a change event on load of the initial data.
              // We only want to update the form state if the change was user-intiated.
              if (e.isInteracted) {
                props.onSubmit &&
                  props.onSubmit(e.value as EnumMultiSelectFieldV2Value);
              }
            }}
            mode={props.growToFit ? "Box" : "Default"}
            disabled={props.disabled}
          />
        </div>
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </FormGroup>
  );
};

export default EnumMultiSelectFieldV2;
